import type { Point } from './orthogonal-router'

/**
 * Minimum fraction of a pipe's arc-length either half of a positional split may have.
 * Proportional, so a long pipe is not needlessly restricted — see [[clamp-split-t]] for
 * why an absolute *screen-px* threshold is wrong (it makes short/zoomed-out pipes
 * unsplittable).
 */
export const SPLIT_T_EPSILON = 0.001

/**
 * A small absolute floor, in WORLD units, on how close to an endpoint a split may land.
 * The fraction alone lets an arbitrarily short pipe still get a sub-pixel sliver; this
 * floor keeps the inserted junction a visible distance off the end. World units, not
 * screen px, so it is zoom-invariant — and the overlap→midpoint rule below guarantees it
 * never makes a pipe unsplittable.
 */
export const SPLIT_ABS_FLOOR = 6

export interface SplitGeometry {
  /** Where the inserted junction sits — on the polyline, clamped off the endpoints. */
  position: Point
  /** Fractional arc-length of the split; drives BOTH length apportioning and geometry. */
  t: number
  /** The near-endpoint guard moved the split away from where it was requested. */
  clamped: boolean
  /** The pipe was too small to honour both guard zones → split at the midpoint. */
  midpoint: boolean
}

function segLen(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y)
}

function arcLength(poly: readonly Point[]): number {
  let total = 0
  for (let i = 1; i < poly.length; i++) total += segLen(poly[i - 1]!, poly[i]!)
  return total
}

/** The point at `frac` of the total arc-length along `poly` (frac clamped to [0,1]). */
function pointAtFraction(poly: readonly Point[], frac: number): Point {
  if (poly.length === 0) return { x: 0, y: 0 }
  const L = arcLength(poly)
  if (poly.length === 1 || L === 0) return { x: poly[0]!.x, y: poly[0]!.y }
  const target = Math.max(0, Math.min(1, frac)) * L
  let acc = 0
  for (let i = 1; i < poly.length; i++) {
    const s = segLen(poly[i - 1]!, poly[i]!)
    if (acc + s >= target) {
      const u = s === 0 ? 0 : (target - acc) / s
      return {
        x: poly[i - 1]!.x + (poly[i]!.x - poly[i - 1]!.x) * u,
        y: poly[i - 1]!.y + (poly[i]!.y - poly[i - 1]!.y) * u,
      }
    }
    acc += s
  }
  return { x: poly[poly.length - 1]!.x, y: poly[poly.length - 1]!.y }
}

/**
 * The geometry-side near-endpoint backstop (§3.2/§3.3).
 *
 * `clampSplitT` guards the LENGTH off the endpoints but deliberately leaves the inserted
 * vertex at the raw click, so a tee/split landing within ε of an endpoint gets a non-zero
 * length yet a junction sitting ON the endpoint — a zero-length sliver in the DRAWING.
 * This derives the split POSITION from the clamped fraction along the actual polyline, so
 * position and length always agree and the sliver cannot occur.
 *
 * ε is a fraction of arc-length with a small absolute floor. When the two end-zones would
 * overlap (a truly tiny pipe) the split falls back to the midpoint with `midpoint: true`
 * rather than refusing — Feature 2/3 must never make a pipe unsplittable.
 */
export function clampSplitGeometry(polyline: readonly Point[], rawT: number): SplitGeometry {
  const t0 = Number.isFinite(rawT) ? rawT : 0.5
  const L = arcLength(polyline)

  // Degenerate: nothing to project onto → midpoint of whatever we were given.
  if (polyline.length < 2 || L === 0) {
    return { position: pointAtFraction(polyline, 0.5), t: 0.5, clamped: true, midpoint: true }
  }

  const eps = Math.max(SPLIT_T_EPSILON, SPLIT_ABS_FLOOR / L)

  // Guard zones overlap (2ε ≥ 1): no valid interior band. Split at the midpoint, flagged.
  if (2 * eps >= 1) {
    return { position: pointAtFraction(polyline, 0.5), t: 0.5, clamped: true, midpoint: true }
  }

  const t = Math.min(1 - eps, Math.max(eps, t0))
  const clamped = !Number.isFinite(rawT) || Math.abs(t - t0) > 1e-12
  return { position: pointAtFraction(polyline, t), t, clamped, midpoint: false }
}
